import React, { useState, useEffect } from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";
import Header from "../../components/Header";
import useApp from "../../store/contexts/AppContext";
import { makeStyles } from "@mui/styles";
import { RoomStyle } from "./RoomStyle";
import { ROOM_MODES } from "../../config";
import UserWebcam from "./UserWebcam";
import WaitForPageLoad from "../../components/WaitForPageLoad";
import WaitingOnAdminInGroup from "../../components/Chats/WaitingOnAdminInGroup/WaitingOnAdminInGroup";
import JoinRoomTeam from "./JoinRoomTeam";
import RoomUserLobby from "./RoomUserLobby";
import RoomTeams from "./RoomTeams";
import ActiveChatInRoom from "../../components/Chats/ActiveChatInRoom";
import { ROUTES } from "../../routes/User/paths";

const useStyles = makeStyles((theme) => RoomStyle(theme));

export default function Room() {
  const classes = useStyles();
  const [roomLoading, setRoomLoading] = useState(true);
  const [isCurrentUserMainAdmin, setIsCurrentUserMainAdmin] = useState(false);
  const [waitingOnAdmin, setWaitingOnAdmin] = useState(false);
  const [showRoomTitle, setShowRoomTitle] = useState(true);
  const [showRoomParticipants, setShowRoomParticipants] = useState(true);
  const [showRoomTeams, setShowRoomTeams] = useState(true);
  const [roomCallParticipants, setRoomCallParticipants] = useState({});
  const [previewingRoomParticipants, setPreviewingRoomParticipants] = useState(
    []
  );
  const [searchedRoomParticipants, setSearchedRoomParticipants] = useState("");
  const [roomTeams, setRoomTeams] = useState([]);
  const [roomModerators, setRoomModerators] = useState([]);
  const [isModeTitleShowing, setIsModeTitleShowing] = useState(true);
  const [activeRoomPanel, setActiveRoomPanel] = useState("chat");
  const { id } = useParams();
  const urlQueryParams = useLocation().search;
  const roomMode = new URLSearchParams(urlQueryParams).get("mode");

  const history = useHistory();

  const {
    appState: { activeChat, dataLoading, userData, roomToolbox },
    fetchARoomByIdApiCall,
    setCurrentUserRoomToolboxEnability,
    cancelJoinRequest,
  } = useApp();

  useEffect(() => {
    if (roomMode === ROOM_MODES.SPECTATOR) {
      history.replace(`${ROUTES.spectate}/${id}`);
    }
  }, [history, id, roomMode]);

  useEffect(() => {
    // Check if media setup is complete and still valid
    const setupComplete = localStorage.getItem("mediaSetupComplete");
    const setupTimestamp = localStorage.getItem("setupTimestamp");
    const thirtyMinutesAgo = Date.now() - 30 * 60 * 1000; // 30 minutes in milliseconds

    // Determine if setup is needed
    const needsSetup =
      !setupComplete ||
      !setupTimestamp ||
      parseInt(setupTimestamp) < thirtyMinutesAgo;

    if (needsSetup) {
      history.push(`/rooms/${id}/setup`);
    }
  }, [id, history]);

  useEffect(() => {
    // Don't hit the join API until media setup is confirmed — otherwise the admin
    // sees a join request from a user who will immediately be redirected away.
    const setupComplete = localStorage.getItem("mediaSetupComplete");
    const setupTimestamp = localStorage.getItem("setupTimestamp");
    const thirtyMinutesAgo = Date.now() - 30 * 60 * 1000;
    const needsSetup =
      !setupComplete ||
      !setupTimestamp ||
      parseInt(setupTimestamp) < thirtyMinutesAgo;
    if (needsSetup) return;

    fetchARoomByIdApiCall(id, false).then(
      ({ status, data }) => {
        //If status is 204, then admin is online and user is waiting for admin to either reject or approve
        if (status === 204) {
          //Admin is online but user is not a participant
          setWaitingOnAdmin(true);
          return true;
        } else if (status === 203) {
          //User has not selected a team yet
          //Only room teams is sent back as data here from the api
          setRoomTeams(data);
          return true;
        }
        setRoomLoading(false);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (dataLoading === true) {
      setTimeout(() => {
        setRoomLoading(false);
      }, 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLoading]);

  // When user navigates away while waiting for admin approval, cancel their request
  useEffect(() => {
    if (!waitingOnAdmin) return;

    const handleBeforeUnload = () => cancelJoinRequest();
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      // Navigation within the app: emit cancellation on unmount
      cancelJoinRequest();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitingOnAdmin]);

  useEffect(() => {
    setTimeout(() => {
      setIsModeTitleShowing(false);
    }, 5000);
  }, []);

  useEffect(() => {
    // const updatedPreviewingRoomParticipants = activeChat?.room_participants?.filter(
    //   (participant) => participant.is_online
    // );
    setPreviewingRoomParticipants(activeChat?.room_participants);
  }, [activeChat.room_participants]);

  useEffect(() => {
    if (
      Object.keys(userData).length > 0 &&
      previewingRoomParticipants?.length > 0
    ) {
      const isCurrentUserMainAdminResult = previewingRoomParticipants?.some(
        (participant) => {
          const participantId = participant?.id;

          if (userData?.id?.toString() === participantId?.toString()) {
            setCurrentUserRoomToolboxEnability(
              participant?.is_toolbox_banned ? false : true
            );
          }

          return (
            Number(participant.is_admin) === 1 &&
            userData?.id?.toString() === participantId?.toString()
          );
        }
      );

      setIsCurrentUserMainAdmin(isCurrentUserMainAdminResult);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, previewingRoomParticipants]);

  useEffect(() => {
    // Set room moderators
    const roomModeratorsIsAdminStatus = [1, 2];
    let getRoomModerators =
      previewingRoomParticipants?.filter((participant) =>
        roomModeratorsIsAdminStatus.includes(participant.is_admin)
      ) || [];
    getRoomModerators = getRoomModerators.map((participant) => ({
      id: participant.id,
      username: participant.username,
      is_admin: participant.is_admin,
    }));
    setRoomModerators(getRoomModerators);
  }, [previewingRoomParticipants]);

  // Set filter option for room on search inputs
  let filteredRoomParticipantList = [];
  const regex = new RegExp(searchedRoomParticipants, "i");

  filteredRoomParticipantList = previewingRoomParticipants?.filter((row) =>
    regex.test(row.username)
  );

  const roomParticipantListToRender = searchedRoomParticipants
    ? filteredRoomParticipantList
    : previewingRoomParticipants;

  const roomTabOptions = [
    { value: "scores", label: "Scores" },
    { value: "chat", label: "Live Chat" },
    { value: "lobby", label: "Users/Lobby List" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
      <Header isCurrentUserMainAdmin={isCurrentUserMainAdmin} />

      {isModeTitleShowing ? (
        <div
          className={classes.modeNotification}
          style={{
            backgroundColor: "#4CAF50",
          }}
        >
          You're on regular mode
        </div>
      ) : null}

      {!waitingOnAdmin && (
        <div id="webcamDisplayCanvas" className={classes.container}>
          <UserWebcam
            roomLoading={roomLoading}
            setShowRoomTitle={setShowRoomTitle}
            showRoomTitle={showRoomTitle}
            setShowRoomParticipants={setShowRoomParticipants}
            showRoomParticipants={showRoomParticipants}
            setShowRoomTeams={setShowRoomTeams}
            showRoomTeams={showRoomTeams}
            isCurrentUserMainAdmin={isCurrentUserMainAdmin}
            roomId={id}
            setRoomCallParticipants={setRoomCallParticipants}
            roomCallParticipants={roomCallParticipants}
            isUserOnSpectatorMode={false}
          />

          <div></div>
        </div>
      )}

      <div className={classes.container2}>
        {roomLoading ? (
          <WaitForPageLoad />
        ) : waitingOnAdmin ? (
          <WaitingOnAdminInGroup />
        ) : roomTeams.length > 0 ? (
          <JoinRoomTeam teams={roomTeams} />
        ) : (
          <div className={classes.roomTabbedPanelWrapper}>
            <div className={classes.roomTabsBar}>
              {roomTabOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`${classes.roomTabButton} ${
                    activeRoomPanel === option.value
                      ? classes.roomTabButtonActive
                      : ""
                  }`}
                  onClick={() => setActiveRoomPanel(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <div className={classes.roomPanelSurface}>
              {activeRoomPanel === "scores" ? (
                showRoomTeams ? (
                  <RoomTeams />
                ) : (
                  <div className={classes.roomPanelEmptyState}>
                    Scores are currently hidden.
                  </div>
                )
              ) : null}

              {activeRoomPanel === "lobby" ? (
                showRoomParticipants ? (
                  <RoomUserLobby
                    setSearchedRoomParticipants={setSearchedRoomParticipants}
                    roomParticipants={roomParticipantListToRender}
                    roomLoading={roomLoading}
                    roomModerators={roomModerators}
                    roomId={id}
                  />
                ) : (
                  <div id="lobbyButton" className={classes.roomPanelEmptyState}>
                    Users/Lobby list is currently hidden.
                  </div>
                )
              ) : null}

              {activeRoomPanel === "chat" ? (
                <ActiveChatInRoom
                  roomLoading={roomLoading}
                  roomModerators={roomModerators}
                />
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
